/**
 * Shells and mines.
 *
 * Shells fly in a straight line on a horizontal plane, ricochet off walls a
 * limited number of times (reflectCircle), and destroy the first tank they touch.
 * A tank is never hit by its OWN shell (no self-ricochet kills, player or enemy).
 * Mines arm after a delay then detonate on contact or fuse-out, dealing a radial
 * blast that spares the tank that laid it.
 */

import * as THREE from "three";
import { BULLET, MINE, SHELL_COLOR, MUZZLE, MECHA } from "../config";
import { reflectCircle, resolveCircle } from "../world/gamemap";
import { type Arena, type FlowStep } from "../world/arena";
import { sfx } from "../engine/sfx";
import type { Effects } from "./effects";
import type { Tank } from "../entities/tank";

// a tapered bullet (narrow nose, wider base), laid along +Z so it can be oriented
// to the shell's velocity each frame. Bigger + clearly "bullet shaped" vs a ball.
const shellGeom = new THREE.CylinderGeometry(0.6, 2.0, 6.5, 12);
shellGeom.rotateX(Math.PI / 2);
const SHELL_HIT = 5.5; // shell-vs-shell collision distance (shoot bullets out of the air)
const shellMatCache = new Map<number, THREE.MeshBasicMaterial>();

/** Squared distance from point (px,pz) to the segment (ax,az)→(bx,bz). Lets the
 *  hit test sweep a shell's whole step against a tank so fast/grazing shots can't
 *  tunnel through. */
function segDist2(px: number, pz: number, ax: number, az: number, bx: number, bz: number): number {
  const dx = bx - ax, dz = bz - az;
  const len2 = dx * dx + dz * dz;
  let t = len2 > 1e-9 ? ((px - ax) * dx + (pz - az) * dz) / len2 : 0;
  t = t < 0 ? 0 : t > 1 ? 1 : t;
  const ex = px - (ax + t * dx), ez = pz - (az + t * dz);
  return ex * ex + ez * ez;
}
function shellMat(color: number): THREE.MeshBasicMaterial {
  let m = shellMatCache.get(color);
  if (!m) {
    m = new THREE.MeshBasicMaterial({ color });
    shellMatCache.set(color, m);
  }
  return m;
}

const mineGeom = new THREE.CylinderGeometry(4, 4.5, 2.4, 14);
// flat unit disc (radius 1, lying on the XZ plane) for the artillery danger-zone marker
const markerGeom = new THREE.CircleGeometry(1, 32);
markerGeom.rotateX(-Math.PI / 2);

// shared rocket projectile geometry (a capsule laid along +Z; oriented per-frame
// to the velocity). Shared so firing a rocket allocates no geometry.
const rocketGeom = new THREE.CapsuleGeometry(1.0, 5.5, 4, 8);
rocketGeom.rotateX(Math.PI / 2);
const Z_AXIS = new THREE.Vector3(0, 0, 1);
const _vdir = new THREE.Vector3();
const _mfDir = new THREE.Vector3(); // scratch: muzzle-flash spark direction (turret forward)
const _flow: FlowStep = { kind: "none", dx: 0, dz: 0, toLevel: 0 }; // scratch for TOW maze-nav
const _cell = { x: 0, z: 0 }; // scratch for snapping a missile onto a lift cell

// artillery (arcing mortar) + TOW (homing missile) projectile tuning
const ARC_COLOR = 0xffb030; // glowing mortar shell
export const ARC_BLAST = 30; // landing blast radius (studs)
const TOW_COLOR = 0xc060ff; // heat-seeker — a distinct hue so the player can spot + shoot it
const TOW_SPEED = 58; // slow enough to dodge / shoot down
const TOW_TURN = 2.7; // homing turn rate (rad/s)
const TOW_LIFE = 12; // long-lived so it can chase the player across a big floor

export interface Shell {
  pos: THREE.Vector3;
  vel: THREE.Vector3;
  bouncesLeft: number;
  bounced: boolean; // has it ricocheted at least once (for the trick-shot achievement)
  life: number;
  owner: Tank;
  byPlayer: boolean;
  team: number; // owner's team — a shell only hits tanks on the OTHER team
  level: number; // shells stay on the level they were fired from
  y: number; // world render height
  floorY: number; // the level's surface height (for bounce-spark FX)
  rocket: boolean; // fast, non-bouncing rocket (dies on first wall hit)
  trailT: number; // rocket trail emit countdown
  traveled: number; // total distance flown (for long-shot scoring)
  bounces: number; // ricochets made so far (for the bounce multiplier)
  damage: number; // shield/health points this shell removes on hit (Punch); 1 = normal
  pierce: number; // how many more incoming bullets this shell can shoot down before it's spent (Dense Rounds); 1 = normal
  mesh: THREE.Mesh;
  dead: boolean;
  /** ARTILLERY lob: a parabolic shell that ignores walls and blasts on landing */
  arc?: { sx: number; sz: number; sy: number; tx: number; tz: number; baseY: number; dur: number; t: number; peak: number };
  /** the artillery round's ground danger-zone marker (telegraphs where it lands) */
  marker?: THREE.Mesh;
  /** TOW: a slow heat-seeking missile that steers toward this target tank */
  homing?: Tank;
  /** `traveled` value at the last lift hop — gates re-hopping so it can't ping-pong */
  lastHop?: number;
  /** a god-aim sniper bank: integrate in FIXED 5-unit steps (matching the bank solver
   *  exactly) so a solved pentabounce lands where predicted, not a barrel-length off */
  precise?: boolean;
  carry?: number; // leftover sub-5 distance carried to the next frame (precise shells)
}

export interface Mine {
  pos: THREE.Vector3;
  owner: Tank;
  byPlayer: boolean;
  level: number;
  floorY: number; // the level's surface height (for the blast FX)
  armAt: number;
  fuseAt: number;
  radius: number; // blast radius (MINE.radius × the layer's mine-radius multiplier — Heavy Mines)
  mesh: THREE.Mesh;
  marker: THREE.Mesh; // red ground danger-ring (telegraphs the blast like artillery)
  dead: boolean;
  lit: boolean; // last blink state — a beep fires on each off→on edge
}

/** A tank destroyed this step + how. */
export interface HitEvent {
  tank: Tank;
  killer?: Tank; // the tank that fired the killing shot/blast (for the kill feed)
  byPlayer: boolean;
  ricochet: boolean;
  mine: boolean;
  dirx: number; // shot travel direction (for death-gib debris); 0 for a mine
  dirz: number;
  traveled: number; // distance the killing shell flew (long-shot scoring); 0 for a mine
  bounces: number; // ricochets in the killing shot (bounce multiplier); 0 for a mine
  absorbed?: boolean; // a shield soaked this hit (the tank lives) — scored, not a kill
  popped?: number; // shields removed by an absorbed hit (Punch can crack several) — scales the score
  shotDown?: boolean; // the player shot an enemy shell/missile out of the air (not a tank kill)
  tow?: boolean; // the shot-down projectile was a TOW homing missile (worth more)
  leg?: boolean; // a hit on a mecha's leg (drained its leg-shield or popped a leg) — scored, not a kill
}

export class Bullets {
  shells: Shell[] = [];
  mines: Mine[] = [];
  private now = 0;
  private arena: Arena | null = null;
  private effects: Effects | null = null;

  constructor(private scene: THREE.Scene) {}

  /** Point shells/mines at the current floor's road network (level-aware bounce). */
  setArena(arena: Arena | null): void {
    this.arena = arena;
  }

  /** Wire the FX system so mine detonations spawn a blast. */
  setEffects(effects: Effects): void {
    this.effects = effects;
  }

  /** A flat ground danger-ring disc (telegraphs an incoming blast — artillery + mines). */
  private makeMarker(x: number, y: number, z: number, radius: number, color: number, opacity = 0.35): THREE.Mesh {
    const m = new THREE.Mesh(markerGeom, new THREE.MeshBasicMaterial({ color, transparent: true, opacity, depthWrite: false, depthTest: false, side: THREE.DoubleSide }));
    m.renderOrder = 4; // ground telegraph — always on top so it can't be missed
    m.position.set(x, y, z);
    m.scale.setScalar(radius);
    this.scene.add(m);
    return m;
  }

  countShells(owner: Tank): number {
    let n = 0;
    for (const s of this.shells) if (!s.dead && s.owner === owner) n++;
    return n;
  }
  countMines(owner: Tank): number {
    let n = 0;
    for (const m of this.mines) if (!m.dead && m.owner === owner) n++;
    return n;
  }

  /** Distinct muzzle flash at the barrel tip for whatever weapon `owner` fires (color/size/sparks per type). */
  private muzzleFX(owner: Tank, m: THREE.Vector3): void {
    if (!this.effects) return;
    const t = owner.type;
    const base = owner.isPlayer ? SHELL_COLOR.player : owner.team === 0 ? SHELL_COLOR.ally : SHELL_COLOR.enemy;
    const d = owner.forward(_mfDir); // turret forward — sparks fan out of the barrel
    if (t?.gatling)         this.effects.muzzleFlash(m.x, m.y, m.z, MUZZLE.gatling, 4, 0.05, 0, d.x, d.z, owner.floorY); // rapid fire: tiny, no sparks
    else if (t?.rocket)     this.effects.muzzleFlash(m.x, m.y, m.z, SHELL_COLOR.rocket, 7, 0.10, 3, d.x, d.z, owner.floorY);
    else if (t?.artillery)  this.effects.muzzleFlash(m.x, m.y, m.z, ARC_COLOR, 9, 0.14, 4, d.x, d.z, owner.floorY); // howitzer: big amber blast
    else if (t?.mlrs)       this.effects.muzzleFlash(m.x, m.y, m.z, MUZZLE.mlrs, 8, 0.12, 4, d.x, d.z, owner.floorY);
    else if (t?.tow)        this.effects.muzzleFlash(m.x, m.y, m.z, TOW_COLOR, 7, 0.11, 3, d.x, d.z, owner.floorY);
    else if (t?.aimBounces) this.effects.muzzleFlash(m.x, m.y, m.z, MUZZLE.sniper, 3, 0.06, 1, d.x, d.z, owner.floorY); // tight focused crack
    else                    this.effects.muzzleFlash(m.x, m.y, m.z, base, 5, 0.07, 2, d.x, d.z, owner.floorY); // normal shell
  }

  fire(owner: Tank, speed: number, bounces: number, rocket = false, life = BULLET.life, damage = 1, precise = false, levelOverride?: number, floorYOverride?: number, pierce = 1): void {
    const muzzle = owner.muzzle(new THREE.Vector3());
    this.muzzleFX(owner, muzzle);
    owner.advanceBarrel(); // multi-barrel turrets alternate per shot
    const dir = owner.forward(new THREE.Vector3());
    const color = rocket ? SHELL_COLOR.rocket : owner.isPlayer ? SHELL_COLOR.player : owner.team === 0 ? SHELL_COLOR.ally : SHELL_COLOR.enemy;
    const mesh = new THREE.Mesh(rocket ? rocketGeom : shellGeom, shellMat(color));
    mesh.position.copy(muzzle);
    // elevated direct shot (e.g. a raised mecha shooting a tank UP on a road): live on the target's
    // level + deck height so it actually registers (collision requires t.level === s.level).
    const y = floorYOverride != null ? floorYOverride + 8 : muzzle.y;
    if (floorYOverride != null) mesh.position.y = y;
    mesh.quaternion.setFromUnitVectors(Z_AXIS, _vdir.set(dir.x, 0, dir.z).normalize());
    this.scene.add(mesh);
    this.shells.push({
      pos: muzzle.clone(),
      vel: dir.multiplyScalar(speed),
      bouncesLeft: bounces,
      bounced: false,
      life,
      owner,
      byPlayer: owner.isPlayer,
      team: owner.team,
      level: levelOverride ?? owner.level,
      y,
      floorY: floorYOverride ?? owner.floorY,
      rocket,
      trailT: 0.05, // small initial delay so no trail puff spawns at the muzzle
      traveled: 0,
      bounces: 0,
      damage,
      pierce: Math.max(1, pierce),
      mesh,
      dead: false,
      precise,
      carry: 0,
    });
  }

  /** ARTILLERY: lob a parabolic shell that ignores walls and blasts where it lands
   *  ((tx,tz) on the target level, ground height baseY). */
  fireArc(owner: Tank, tx: number, tz: number, targetLevel: number, baseY: number): void {
    const muzzle = owner.muzzle(new THREE.Vector3());
    this.muzzleFX(owner, muzzle);
    owner.advanceBarrel();
    const dist = Math.hypot(tx - muzzle.x, tz - muzzle.z);
    const dur = Math.max(0.8, Math.min(2.4, dist / 175)); // longer lobs take longer
    const peak = Math.max(70, Math.min(230, 60 + dist * 0.32)); // and arc higher
    const mesh = new THREE.Mesh(shellGeom, shellMat(ARC_COLOR));
    mesh.scale.setScalar(1.5); // chunkier mortar round
    mesh.position.copy(muzzle);
    this.scene.add(mesh);
    // ground danger-zone disc at the landing spot (telegraphs the blast)
    const marker = this.makeMarker(tx, baseY + 0.6, tz, ARC_BLAST, 0xff2a18);
    this.shells.push({
      pos: muzzle.clone(), vel: new THREE.Vector3(),
      bouncesLeft: 0, bounced: false, life: dur + 0.2, owner, byPlayer: owner.isPlayer, team: owner.team,
      level: targetLevel, y: muzzle.y, floorY: baseY, rocket: false, trailT: 0.05,
      traveled: 0, bounces: 0, damage: 1, pierce: 1, mesh, dead: false, marker,
      arc: { sx: muzzle.x, sz: muzzle.z, sy: muzzle.y, tx, tz, baseY, dur, t: 0, peak },
    });
  }

  /** TOW: fire a slow heat-seeking missile that steers toward `target`. */
  fireHoming(owner: Tank, target: Tank): void {
    const muzzle = owner.muzzle(new THREE.Vector3());
    this.muzzleFX(owner, muzzle);
    owner.advanceBarrel(); // TOW: alternate the x2/x3 barrels per missile
    const dir = owner.forward(new THREE.Vector3());
    const mesh = new THREE.Mesh(rocketGeom, shellMat(TOW_COLOR));
    mesh.scale.setScalar(1.4);
    mesh.position.copy(muzzle);
    mesh.quaternion.setFromUnitVectors(Z_AXIS, _vdir.set(dir.x, 0, dir.z).normalize());
    this.scene.add(mesh);
    this.shells.push({
      pos: muzzle.clone(), vel: dir.multiplyScalar(TOW_SPEED),
      bouncesLeft: 0, bounced: false, life: TOW_LIFE, owner, byPlayer: owner.isPlayer, team: owner.team,
      level: owner.level, y: muzzle.y, floorY: owner.floorY, rocket: false, trailT: 0.05,
      traveled: 0, bounces: 0, damage: 1, pierce: 1, mesh, dead: false, homing: target,
    });
  }

  /** A radial explosion at (x,z) on `level`: blast FX + kill every tank in `radius`
   *  except `owner`. Shared by mines and artillery landings. */
  private detonate(x: number, z: number, level: number, floorY: number, byPlayer: boolean, owner: Tank, tanks: Tank[], events: HitEvent[], radius: number): void {
    this.effects?.mineBlast(x, floorY, z, radius);
    sfx.play("explode", { x, y: floorY, z });
    for (const t of tanks) {
      if (!t.alive || t.riding || t.thrown) continue;
      if (t.team === owner.team) continue; // blast only harms the other team (covers the owner)
      if (t.level !== level) continue;
      if (t.invulnT > 0) continue; // Aegis shield blocks the blast too
      if (t.isMecha && !t.crouched) continue; // a striding walker is immune to blasts (legs only via direct shots)
      const dx = t.pos.x - x, dz = t.pos.z - z;
      if (dx * dx + dz * dz <= radius * radius) {
        t.health = 0;
        t.alive = false;
        t.killer = owner;
        events.push({ tank: t, killer: owner, byPlayer, ricochet: false, mine: true, dirx: 0, dirz: 0, traveled: 0, bounces: 0 });
      }
    }
  }

  /** Turn a homing missile's velocity toward its target, limited by TOW_TURN. When
   *  far, it NAVIGATES the maze via the player's flow field (around obstacles);
   *  once close (or no route), it homes straight in. */
  private steerHoming(s: Shell, sub: number): void {
    const tgt = s.homing!;
    if (!tgt.alive) return; // target gone — coast straight
    let dx = tgt.pos.x - s.pos.x, dz = tgt.pos.z - s.pos.z;
    const td = Math.hypot(dx, dz);
    if (td < 1e-3) return;
    dx /= td; dz /= td;
    // follow the maze route while far; the flow field BFSes from the quarry each
    // frame, so it gives a direction toward them AROUND walls. An ALLY missile
    // (team 0) hunts the enemy team → ally flow; an enemy missile hunts the
    // player/mercs → enemy flow. (Without this, ally TOWs steered toward their OWN
    // side and never tracked enemies.)
    if (this.arena && td > 56) {
      if (s.team === 0) this.arena.allyFlowStep(s.pos.x, s.pos.z, s.level, _flow);
      else this.arena.flowStep(s.pos.x, s.pos.z, s.level, _flow);
      if (_flow.kind === "move") { dx = _flow.dx; dz = _flow.dz; }
      else if (_flow.kind === "lift" && s.traveled - (s.lastHop ?? -1e9) > 40) {
        // a TOW missile RIDES the lift to follow the player across a divided floor
        // (flowStep only returns "lift" when it shortens the route). Snap onto the
        // lift cell + hop to the destination level; keep homing from there. The
        // travel-gate stops it re-triggering the same pad and ping-ponging levels.
        this.arena.cellCenter(s.pos.x, s.pos.z, _cell);
        s.pos.x = _cell.x; s.pos.z = _cell.z;
        s.level = _flow.toLevel;
        s.y = s.floorY + this.arena.levelHeight(s.level) + 8;
        s.lastHop = s.traveled;
      }
    }
    const sp = Math.hypot(s.vel.x, s.vel.z) || 1;
    const cx = s.vel.x / sp, cz = s.vel.z / sp;       // current heading
    const ang = Math.atan2(cx * dz - cz * dx, cx * dx + cz * dz); // signed turn toward desired
    const max = TOW_TURN * sub;
    const turn = ang < -max ? -max : ang > max ? max : ang;
    const co = Math.cos(turn), si = Math.sin(turn);
    s.vel.x = (cx * co - cz * si) * sp;
    s.vel.z = (cx * si + cz * co) * sp;
  }

  /** Advance an artillery arc shell; blast on landing. */
  private updateArc(s: Shell, dt: number, tanks: Tank[], events: HitEvent[]): void {
    const a = s.arc!;
    a.t += dt;
    const f = Math.min(1, a.t / a.dur);
    s.pos.x = a.sx + (a.tx - a.sx) * f;
    s.pos.z = a.sz + (a.tz - a.sz) * f;
    s.pos.y = a.sy + (a.baseY - a.sy) * f + 4 * f * (1 - f) * a.peak; // lerp height + parabolic hump
    s.y = s.pos.y;
    s.mesh.position.set(s.pos.x, s.pos.y, s.pos.z);
    // point the round along its INSTANTANEOUS arc velocity (pitches up, then down) —
    // dy/dt of the height curve; horizontal components are constant over the lob.
    const vx = (a.tx - a.sx) / a.dur, vz = (a.tz - a.sz) / a.dur;
    const vy = ((a.baseY - a.sy) + 4 * a.peak * (1 - 2 * f)) / a.dur;
    s.mesh.quaternion.setFromUnitVectors(Z_AXIS, _vdir.set(vx, vy, vz).normalize());
    if (s.marker) { // the danger zone FADES IN — opacity = fraction of flight elapsed, so it hits
      const m = s.marker.material as THREE.MeshBasicMaterial; // fully opaque (0% transparent) exactly when it lands
      m.opacity = f; // (a fainter ring = more time to clear it; the player can read time-to-impact off the opacity)
    }
    if (f >= 1) {
      this.detonate(a.tx, a.tz, s.level, a.baseY, s.byPlayer, s.owner, tanks, events, ARC_BLAST);
      this.killShell(s);
    }
  }

  layMine(owner: Tank, instant = false, radiusMul = 1): void {
    const radius = MINE.radius * radiusMul; // Heavy Mines widens the blast + the mine body
    const mesh = new THREE.Mesh(mineGeom, new THREE.MeshLambertMaterial({ color: 0x33363b }));
    mesh.position.set(owner.pos.x, owner.floorY + 1.2, owner.pos.z);
    mesh.scale.setScalar(radiusMul); // a bigger blast → a visibly bigger mine
    this.scene.add(mesh);
    // red ground danger-ring covering the (now much bigger) blast — like the artillery telegraph
    const marker = this.makeMarker(owner.pos.x, owner.floorY + 0.6, owner.pos.z, radius, 0xff2a18, 0.22);
    this.mines.push({
      pos: new THREE.Vector3(owner.pos.x, owner.floorY + 1.2, owner.pos.z),
      owner,
      byPlayer: owner.isPlayer,
      level: owner.level,
      floorY: owner.floorY,
      // instant = a kamikaze's detonation: already armed and fuse-out, so it blasts on the next update
      armAt: instant ? 0 : this.now + MINE.armTime,
      fuseAt: instant ? this.now : this.now + MINE.fuse,
      radius,
      mesh,
      marker,
      dead: false,
      lit: false,
    });
  }

  /** MECHA: nearest ALIVE leg whose foot the shell's swept segment grazes, or -1. */
  private mechaLegHit(t: Tank, ax: number, az: number, bx: number, bz: number, shellR: number): number {
    const rr = (MECHA.legHitR + shellR) ** 2;
    let best = -1, bestD = rr;
    for (let i = 0; i < 4; i++) {
      const w = t.legWorld[i];
      if (!w.alive) continue;
      const d = segDist2(w.x, w.z, ax, az, bx, bz);
      if (d <= bestD) { bestD = d; best = i; }
    }
    return best;
  }

  /** MECHA: advance any walker that has lost 2 legs through its short fall, then EXPLODE it
   *  — an AoE blast that spares its own team, and the walker itself dies (→ the normal cull
   *  fires the kill feed + this kill event drives gib/score/cash). */
  private mechaTopples(dt: number, tanks: Tank[], events: HitEvent[]): void {
    const TOPPLE_TIME = 0.7;
    for (const t of tanks) {
      if (!t.isMecha || !t.toppling || !t.alive) continue;
      t.toppleT += dt / TOPPLE_TIME;
      if (t.toppleT < 1) continue;
      t.toppleT = 1;
      this.effects?.mineBlast(t.pos.x, t.floorY, t.pos.z, MECHA.aoeR);
      sfx.play("explode", t.pos);
      const r2 = MECHA.aoeR * MECHA.aoeR;
      for (const o of tanks) {
        if (o === t || !o.alive || o.riding || o.thrown || o.level !== t.level) continue;
        if (o.team === t.team) continue;
        if (o.invulnT > 0) continue;
        if (o.isMecha && !o.crouched) continue; // a striding walker shrugs off the blast (legs only via shots)
        if ((o.pos.x - t.pos.x) ** 2 + (o.pos.z - t.pos.z) ** 2 > r2) continue;
        o.health = 0; o.alive = false; o.killer = t;
        events.push({ tank: o, killer: t, byPlayer: false, ricochet: false, mine: true, dirx: 0, dirz: 0, traveled: 0, bounces: 0 });
      }
      t.alive = false; // the walker is destroyed → cull (feed) + this event (gib/score/cash)
      events.push({ tank: t, killer: t.killer, byPlayer: t.killer?.isPlayer ?? false, ricochet: false, mine: true, dirx: 0, dirz: 0, traveled: 0, bounces: 0 });
    }
  }

  /** Advance shells + mines; return the tanks destroyed this step. */
  update(dt: number, tanks: Tank[]): HitEvent[] {
    this.now += dt;
    const events: HitEvent[] = [];
    const hitR = 11; // base tank hit radius (footprint ~20 wide); shell radius added on top
    this.mechaTopples(dt, tanks, events); // advance any toppling walkers → fall + explode

    // --- shells ---
    for (const s of this.shells) {
      if (s.dead) continue;
      s.life -= dt;
      if (s.life <= 0) { this.killShell(s); continue; }
      if (s.arc) { this.updateArc(s, dt, tanks, events); continue; } // artillery lob — own flight path
      // rockets + homing missiles puff a faint trail as they streak across
      if (s.rocket || s.homing) { s.trailT -= dt; if (s.trailT <= 0) { this.effects?.trail(s.pos.x, s.y, s.pos.z, s.floorY); s.trailT = 0.05; } }
      // integrate in substeps for clean wall reflection. A PRECISE sniper bank uses
      // FIXED 5-unit world steps (the exact step the bank solver used), carrying the
      // remainder across frames, so the shell traces the solver's path bounce-for-bounce
      // and lands where predicted. Other shells scale substeps with speed (≤8 units) so
      // fast (5×) shells still can't tunnel a wall.
      let steps: number, sub: number;
      const spd = Math.hypot(s.vel.x, s.vel.z) || 1;
      if (s.precise) {
        const dist = spd * dt + (s.carry ?? 0);
        steps = Math.floor(dist / 5);
        s.carry = dist - steps * 5;
        sub = 5 / spd; // each substep advances exactly 5 world units
      } else {
        steps = Math.max(2, Math.ceil((spd * dt) / 8));
        sub = dt / steps;
      }
      const r = (s.rocket ? 14 : hitR) + BULLET.radius; // hit radius incl. the shell's own size
      for (let i = 0; i < steps && !s.dead; i++) {
        if (s.homing) this.steerHoming(s, sub); // heat-seek toward the target before moving
        const ax = s.pos.x, az = s.pos.z; // step start (for the swept hit test)
        s.pos.addScaledVector(s.vel, sub);
        s.traveled += Math.hypot(s.pos.x - ax, s.pos.z - az); // for long-shot scoring
        // tank hits FIRST — sweep the whole step against each same-level tank so a
        // fast or grazing shot can never tunnel past. Shell spent on contact.
        for (const t of tanks) {
          if (!t.alive || t.riding || t.thrown) continue;
          if (t.team === s.team) continue; // friendly fire OFF — a shell only hits the other team (covers its own owner)
          if (t.level !== s.level) continue;
          // MECHA: a RAISED walker is hittable ONLY at its 4 ground legs (the body towers out
          // of reach); a CROUCHED walker exposes its lowered base as the weak point.
          if (t.isMecha && !t.crouched) {
            const li = this.mechaLegHit(t, ax, az, s.pos.x, s.pos.z, BULLET.radius);
            if (li < 0) continue;
            this.killShell(s);
            this.effects?.shieldPop(t.legWorld[li].x, t.floorY + 4, t.legWorld[li].z, t.floorY);
            sfx.play("deny");
            if (t.legShield > 0) {
              t.legShield = Math.max(0, t.legShield - s.damage); // shields absorb leg hits before any leg can pop
            } else {
              t.legHp[li] -= s.damage;
              if (t.legHp[li] <= 0) { t.loseLeg(li); if (t.toppling) t.killer = s.owner; } // 2nd leg → topple (resolved in update())
            }
            // report the leg hit so the shooter can be scored for it (not a kill — the walker lives)
            const ld = Math.hypot(s.vel.x, s.vel.z) || 1;
            events.push({ tank: t, killer: s.owner, byPlayer: s.byPlayer, ricochet: s.bounced, mine: false, dirx: s.vel.x / ld, dirz: s.vel.z / ld, traveled: s.traveled, bounces: s.bounces, leg: true });
            break;
          }
          const bodyR = t.isMecha ? MECHA.baseHitR + BULLET.radius : r; // crouched mecha base vs normal footprint
          if (segDist2(t.pos.x, t.pos.z, ax, az, s.pos.x, s.pos.z) <= bodyR * bodyR) {
            const dl = Math.hypot(s.vel.x, s.vel.z) || 1;
            this.killShell(s);
            if (t.invulnT > 0) { // an Aegis is shielding this tank — the shell fizzles, no damage
              this.effects?.shieldPop(t.pos.x, t.floorY + 10, t.pos.z, t.floorY);
              sfx.play("deny");
              break;
            }
            t.health -= s.damage; // Punch removes multiple shield/health points at once
            if (t.health <= 0) {
              t.alive = false;
              t.killer = s.owner;
              events.push({ tank: t, killer: s.owner, byPlayer: s.byPlayer, ricochet: s.bounced, mine: false, dirx: s.vel.x / dl, dirz: s.vel.z / dl, traveled: s.traveled, bounces: s.bounces });
            } else {
              sfx.play("deny"); // shield break — same full-volume cue as the player's shield (not positional, or it's inaudible across a big floor)
              t.absorbHit();
              this.effects?.shieldPop(t.pos.x, t.floorY + 10, t.pos.z, t.floorY); // blue shield flare
              // report the shield pop so the player can be scored for it (not a kill).
              // `popped` = shields removed this hit (Punch can crack several at once) → scaled score.
              events.push({ tank: t, byPlayer: s.byPlayer, ricochet: s.bounced, mine: false, dirx: s.vel.x / dl, dirz: s.vel.z / dl, traveled: s.traveled, bounces: s.bounces, absorbed: true, popped: s.damage });
            }
            break;
          }
        }
        if (s.dead) break;
        if (s.homing) { // missiles slide off the PERIMETER (no bounce); roads have no railings → they may fly off a deck edge
          resolveCircle(s.pos, BULLET.radius, s.level === 0, true); // skipWater: missiles fly OVER water too
          continue;
        }
        // walls: shells bounce off the PERIMETER (+ ground solid blocks at level 0). Roads have NO
        // railings now — a shell that crosses a deck edge keeps flying at its height (no fall).
        const hitWall = reflectCircle(s.pos, s.vel, BULLET.radius, s.level === 0);
        if (hitWall) {
          if (s.rocket) { // rockets don't bounce — they detonate on the wall
            this.effects?.spark(s.pos.x, s.y, s.pos.z, s.vel.x, s.vel.z, s.floorY);
            sfx.play("explode", s.pos);
            this.killShell(s); break;
          }
          if (s.bouncesLeft <= 0) { this.killShell(s); break; } // spent on the wall, no bounce sound
          s.bounced = true;
          s.bounces++;
          s.bouncesLeft--;
          sfx.play("bounce");
          this.effects?.spark(s.pos.x, s.y, s.pos.z, s.vel.x, s.vel.z, s.floorY); // ricochet sparks
        }
      }
      if (!s.dead) {
        s.mesh.position.set(s.pos.x, s.y, s.pos.z);
        s.mesh.quaternion.setFromUnitVectors(Z_AXIS, _vdir.set(s.vel.x, 0, s.vel.z).normalize());
      }
    }

    // --- shell-vs-shell: shells on the same level destroy each other (you can
    //     shoot an incoming bullet out of the air) ---
    for (let i = 0; i < this.shells.length; i++) {
      const a = this.shells[i];
      if (a.dead) continue;
      for (let j = i + 1; j < this.shells.length; j++) {
        const b = this.shells[j];
        // skip same-owner shells so your own ricochet can't eat your fresh shot;
        // arc (artillery) rounds are mortars in the air — not shootable
        if (b.dead || a.arc || b.arc || b.level !== a.level || b.team === a.team) continue; // only opposite-team shells collide (same-team covers same-owner)
        if (!a.byPlayer && !b.byPlayer) continue; // only the PLAYER shoots projectiles down — don't let enemy fire silently eat a mercenary's shells
        const dx = a.pos.x - b.pos.x, dz = a.pos.z - b.pos.z;
        if (dx * dx + dz * dz <= SHELL_HIT * SHELL_HIT) {
          this.effects?.spark((a.pos.x + b.pos.x) / 2, a.y, (a.pos.z + b.pos.z) / 2, a.vel.x, a.vel.z, a.floorY);
          sfx.play("bounce", a.pos);
          // exactly one of the pair is the player's shell, the other an enemy projectile
          const playerShell = a.byPlayer ? a : b, enemyShell = a.byPlayer ? b : a;
          // player shot an enemy projectile out of the air → score it (missiles worth more)
          events.push({ tank: enemyShell.owner, byPlayer: true, ricochet: false, mine: false, dirx: 0, dirz: 0, traveled: 0, bounces: 0, shotDown: true, tow: !!enemyShell.homing });
          this.killShell(enemyShell);
          // Dense Rounds: the player's shell punches through and flies on until its pierce budget is spent
          playerShell.pierce -= 1;
          if (playerShell.pierce <= 0) this.killShell(playerShell);
          break;
        }
      }
    }

    // --- mines ---
    for (const m of this.mines) {
      if (m.dead) continue;
      const armed = this.now >= m.armAt;
      // blink faster as the fuse runs down
      const tl = m.fuseAt - this.now;
      const mat = m.mesh.material as THREE.MeshLambertMaterial;
      const lit = armed && Math.sin(this.now * (tl < 1.2 ? 24 : 8)) > 0;
      mat.color.setHex(lit ? 0xff5a4a : 0x33363b);
      if (lit && !m.lit) sfx.play("minebeep", m.pos); // beep on each blink-on (speeds up as it nears the fuse)
      m.lit = lit;
      // the red danger-ring FADES IN over the fuse — fully opaque (0% transparent) exactly at the
      // detonation, so its opacity reads as time-to-blast (the mine BODY still blinks/beeps separately)
      (m.marker.material as THREE.MeshBasicMaterial).opacity = Math.max(0, Math.min(1, 1 - tl / MINE.fuse));

      let blow = this.now >= m.fuseAt;
      if (!blow && armed) {
        for (const t of tanks) {
          if (!t.alive || t.riding || t.thrown) continue;
          if (t.team === m.owner.team) continue; // friendly contact (owner + allies) never triggers it — only the enemy team + the fuse do
          if (t.level !== m.level) continue;
          const dx = t.pos.x - m.pos.x, dz = t.pos.z - m.pos.z;
          if (dx * dx + dz * dz <= 12 * 12) { blow = true; break; }
        }
      }
      if (blow) {
        // lethal to any tank in radius on its level (ignores armour), except the owner
        this.detonate(m.pos.x, m.pos.z, m.level, m.floorY, m.byPlayer, m.owner, tanks, events, m.radius);
        this.killMine(m);
      }
    }

    // compact dead
    this.shells = this.shells.filter((s) => !s.dead);
    this.mines = this.mines.filter((m) => !m.dead);
    return events;
  }

  /** Position of every armed-or-not mine (for blast FX hookup / AI avoidance). */
  minePositions(): THREE.Vector3[] {
    return this.mines.filter((m) => !m.dead).map((m) => m.pos);
  }

  private killShell(s: Shell): void {
    s.dead = true;
    this.scene.remove(s.mesh);
    if (s.marker) { this.scene.remove(s.marker); (s.marker.material as THREE.Material).dispose(); s.marker = undefined; }
  }
  private killMine(m: Mine): void {
    m.dead = true;
    this.scene.remove(m.mesh);
    (m.mesh.material as THREE.Material).dispose();
    this.scene.remove(m.marker);
    (m.marker.material as THREE.Material).dispose();
  }

  clear(): void {
    for (const s of this.shells) { this.scene.remove(s.mesh); if (s.marker) { this.scene.remove(s.marker); (s.marker.material as THREE.Material).dispose(); } }
    for (const m of this.mines) { this.scene.remove(m.mesh); (m.mesh.material as THREE.Material).dispose(); this.scene.remove(m.marker); (m.marker.material as THREE.Material).dispose(); }
    this.shells = [];
    this.mines = [];
  }
}
